import React, { useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import EnhancedRichTextEditor from './EnhancedRichTextEditor';
import { extractTrustComments } from '../utils/trustCitationUtils';
import TrustSourceCommentsPanel from './TrustSourceCommentsPanel';

/**
 * Dialog for previewing document content in a read-only styled view.
 * Uses the same TipTap editor as the authoring experience so all node
 * types (mermaid diagrams, tables, images, template fields, etc.) render correctly.
 * Full screen on phones.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {Function} props.onClose - Callback to close the dialog.
 * @param {string} props.title - Document title shown in the header.
 * @param {string} [props.subtitle] - Secondary line under the title (project, date).
 * @param {string} props.content - HTML content to preview.
 * @param {'document'|'template'} [props.mode='document'] - 'template' renders AI fields and variables.
 * @param {React.ReactNode} [props.actions] - Optional extra action buttons (primary last).
 * @param {string} [props.maxWidth] - MUI Dialog maxWidth.
 * @param {boolean} [props.fullWidth] - MUI Dialog fullWidth.
 */
const DocumentPreviewDialog = ({
  open,
  onClose,
  title,
  subtitle = '',
  content,
  mode = 'document',
  actions = null,
  maxWidth = 'xl',
  fullWidth = true,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // Only parse trust comments while the dialog is visible.
  const trustComments = useMemo(() => (open ? extractTrustComments(content) : []), [open, content]);
  const previewEditorRef = useRef(null);
  const titleId = 'document-preview-title';

  const handleScrollToSection = useCallback((sectionIndex) => {
    previewEditorRef.current?.scrollToTrustSection?.(sectionIndex);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      aria-labelledby={titleId}
      sx={{
        '& .MuiDialog-paper': {
          height: fullScreen ? '100%' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1.5,
          px: { xs: 2, sm: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id={titleId}
            component="h2"
            sx={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" component="p" noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Tooltip title="Close">
          <IconButton onClick={onClose} aria-label="Close preview">
            <Close />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          overflow: 'auto',
          bgcolor: 'var(--q-canvas)',
          px: { xs: 1, sm: 3 },
          py: { xs: 1.5, sm: 3 },
        }}
      >
        {trustComments.length > 0 && (
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              display: { xs: 'none', md: 'block' },
              position: 'sticky',
              top: 0,
              maxHeight: 'min(70vh, calc(90vh - 120px))',
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 0.25,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <TrustSourceCommentsPanel
              comments={trustComments}
              onScrollToSection={handleScrollToSection}
              compact
            />
          </Box>
        )}
        {open && (
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 400 }}>
            <EnhancedRichTextEditor
              ref={previewEditorRef}
              mode={mode}
              documentKey={`${mode}:${content || ''}`}
              initialContent={content || ''}
              readOnly
              showToolbar={false}
              showOutline={false}
              showStatusBar={false}
              minHeight={400}
              enableImages
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions disableSpacing sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        {actions}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
